import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedSummary } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface SummaryStore {
  summaries: SavedSummary[]
  folders: string[]
  addSummary: (summary: SavedSummary) => void
  updateSummary: (id: string, summary: Partial<SavedSummary>) => void
  removeSummary: (id: string) => void
  getSummary: (id: string) => SavedSummary | undefined
  clearSummaries: () => void
  addFolder: (folderName: string) => void
  removeFolder: (folderName: string) => void
  moveSummaryToFolder: (summaryId: string, folderId: string | null) => void
  importSummaries: (summaries: SavedSummary[]) => void
  exportSummaries: () => SavedSummary[]
}

const MAX_SUMMARIES = 50

export const useSummaryStore = create<SummaryStore>()(
  persist(
    (set, get) => ({
      summaries: [],
      folders: [],
      
      addSummary: (summary) => set((state) => {
        // 最大数チェック
        if (state.summaries.length >= MAX_SUMMARIES) {
          // 最古のものを削除
          const sortedSummaries = [...state.summaries].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          )
          sortedSummaries.shift()
          return {
            summaries: [...sortedSummaries, summary]
          }
        }
        
        return {
          summaries: [...state.summaries, summary]
        }
      }),
      
      updateSummary: (id, updates) => set((state) => ({
        summaries: state.summaries.map((s) =>
          s.id === id 
            ? { ...s, ...updates, updatedAt: new Date().toISOString() }
            : s
        )
      })),
      
      removeSummary: (id) => set((state) => ({
        summaries: state.summaries.filter((s) => s.id !== id)
      })),
      
      getSummary: (id) => {
        const state = get()
        return state.summaries.find((s) => s.id === id)
      },
      
      clearSummaries: () => set({ summaries: [] }),
      
      addFolder: (folderName) => set((state) => {
        if (state.folders.includes(folderName)) {
          return state
        }
        return {
          folders: [...state.folders, folderName]
        }
      }),
      
      removeFolder: (folderName) => set((state) => ({
        folders: state.folders.filter((f) => f !== folderName),
        // フォルダ内の要約のfolderId をnullに
        summaries: state.summaries.map((s) =>
          s.folderId === folderName
            ? { ...s, folderId: undefined }
            : s
        )
      })),
      
      moveSummaryToFolder: (summaryId, folderId) => set((state) => ({
        summaries: state.summaries.map((s) =>
          s.id === summaryId
            ? { ...s, folderId, updatedAt: new Date().toISOString() }
            : s
        )
      })),
      
      importSummaries: (summaries) => set((state) => {
        const existingIds = new Set(state.summaries.map(s => s.id))
        const newSummaries = summaries.filter(s => !existingIds.has(s.id))
        
        // 最大数を超えないように調整
        const totalCount = state.summaries.length + newSummaries.length
        if (totalCount > MAX_SUMMARIES) {
          const keepCount = MAX_SUMMARIES - newSummaries.length
          const sortedExisting = [...state.summaries].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          return {
            summaries: [...sortedExisting.slice(0, keepCount), ...newSummaries]
          }
        }
        
        return {
          summaries: [...state.summaries, ...newSummaries]
        }
      }),
      
      exportSummaries: () => {
        const state = get()
        return state.summaries
      }
    }),
    {
      name: 'summary-store',
      storage: createIndexedDBStorage('summary-store'),
      version: 1,
      partialize: (state) => ({
        summaries: state.summaries,
        folders: state.folders
      })
    }
  )
)